<template>
  <b-row>
    <b-col>
      <md-progress-bar md-mode="indeterminate" v-if="loadingBreakdown"></md-progress-bar>

      <div v-else-if="errBreakdown">{{errBreakdown}}</div>

      <div v-else>
        <md-list :md-expand-single="true">
          <md-list-item md-expand :md-expanded.sync="expandNews">
            <md-icon>dashboard</md-icon>
            <span class="md-list-item-text">{{i18n_local.breakdown}}</span>

            <md-list slot="md-expand">
              <div v-if="showBookingFee">
                <p v-if="dueNow=='booking'" class="payment-timeline-subheader highlighted">
                  <md-icon>play_arrow</md-icon>
                  {{bookingTitle}}
                </p>
                <p v-else class="payment-timeline-subheader subdued">
                  <md-icon>check</md-icon>
                  {{bookingTitle}}
                </p>
                <b-row class="moow-list-item">
                  <b-col>
                    <span class="align-middle moow-list-key">{{i18n_local.booking_fee}}</span>
                    <info-toggle :infoContent="i18n_local.booking_fee_info_content"></info-toggle>
                  </b-col>
                  <b-col class="text-right">
                    <span
                      class="align-middle"
                    >{{costBreakdown.bookingFee | toMoney}}{{currencySign}}</span>
                  </b-col>
                </b-row>
              </div>

              <!-- BEFORE MOVING IN -->
              <p v-if="dueNow=='before'" class="payment-timeline-subheader highlighted">
                <md-icon>play_arrow</md-icon>
                {{i18n_local.before_moving_in}}
              </p>
              <p
                v-else-if="dueNow=='booking'"
                class="payment-timeline-subheader subdued"
              >{{i18n_local.before_moving_in}}</p>
              <p v-else class="payment-timeline-subheader subdued">
                <md-icon>check</md-icon>
                {{i18n_local.before_moving_in}}
              </p>

              <div v-if="terms.monthly">
                <b-row class="moow-list-item" v-if="costBreakdown.preDaysRent">
                  <b-col>
                    <span
                      class="align-middle moow-list-key"
                    >{{costBreakdown.preMonth | capitalize}}</span>
                  </b-col>
                  <b-col class="text-right" v-if="costBreakdown.rentForFirstMonth">
                    <span
                      class="align-middle"
                    >{{costBreakdown.rentForFirstMonth | toMoney}}{{currencySign}}</span>
                  </b-col>
                  <b-col class="text-right" v-else>
                    <span
                      class="align-middle"
                    >{{costBreakdown.preDaysRent.total | toMoney}}{{currencySign}}</span>
                  </b-col>
                </b-row>
              </div>
              <div v-else>
                <!-- INFO: if DAILY, all is paid up front -->
                <b-row class="moow-list-item" v-if="costBreakdown.dailyRentTotal">
                  <b-col>
                    <span class="align-middle moow-list-key">{{i18n_local.total_rent}}</span>
                  </b-col>
                  <b-col class="text-right">
                    <span
                      class="align-middle"
                    >{{costBreakdown.dailyRentTotal | toMoney}}{{currencySign}}</span>
                  </b-col>
                </b-row>
              </div>
              <b-row class="moow-list-item" v-if="costBreakdown.deposit">
                <b-col>
                  <span class="align-middle moow-list-key">{{i18n_local.deposit}}</span>
                </b-col>
                <b-col class="text-right">
                  <span class="align-middle">{{costBreakdown.deposit | toMoney}}{{currencySign}}</span>
                </b-col>
              </b-row>

              <!-- ADDITIONAL FEES -->
              <b-row
                class="moow-list-item"
                v-for="(item, index) in terms.additionalFees"
                :key="item+index"
              >
                <div v-if="item.desc">
                  <b-col>
                    <span class="align-middle moow-list-key">{{item.desc}}</span>
                  </b-col>
                  <b-col class="text-right">
                    <span class="align-middle">{{item.fee | toMoney}}{{currencySign}}</span>
                  </b-col>
                </div>
              </b-row>

              <b-row class="moow-list-item" style="font-weight: bold;">
                <b-col>
                  <span class="align-middle moow-list-key">{{i18n_local.total}}</span>
                </b-col>
                <b-col class="text-right">
                  <span
                    class="align-middle"
                  >{{costBreakdown.totalBeforeMovingIn | toMoney}}{{currencySign}}</span>
                </b-col>
              </b-row>

              <!-- DURING STAY -->
              <div v-if="terms.monthly">
                <p v-if="dueNow=='during'" class="payment-timeline-subheader highlighted">
                  <md-icon>play_arrow</md-icon>
                  {{i18n_local.during_your_stay}}
                </p>
                <p
                  v-else-if="dueNow=='before' || dueNow=='during' || dueNow=='booking'"
                  class="payment-timeline-subheader subdued"
                >{{i18n_local.during_your_stay}}</p>
                <p v-else class="payment-timeline-subheader subdued">
                  <md-icon>check</md-icon>
                  {{i18n_local.during_your_stay}}
                </p>
                <p v-if="terms.accountingPeriod" class="moow-info-txt secondary">Period is {{getAccountingPeriod(terms)}}</p>
                <b-row class="moow-list-item">
                  <b-col>
                    <span
                      class="align-middle moow-list-key"
                      v-if="costBreakdown.discountedPrice"
                    >{{i18n_local.monthly_cost_discounted}}</span>
                    <span class="align-middle moow-list-key" v-else>{{i18n_local.monthly_cost}}</span>
                  </b-col>
                  <b-col class="text-right">
                    <span
                      class="align-middle"
                      v-if="costBreakdown.discountedPrice"
                    >{{costBreakdown.discountedPrice | toMoney}}{{currencySign}}</span>
                    <span
                      class="align-middle"
                      v-else
                    >{{costBreakdown.price | toMoney}}{{currencySign}}</span>
                  </b-col>
                </b-row>
                <b-row class="moow-list-item last" v-if="costBreakdown.postMonth">
                  <b-col>
                    <span
                      class="align-middle moow-list-key"
                    >{{costBreakdown.postMonth | capitalize}}</span>
                  </b-col>
                  <b-col class="text-right">
                    <span
                      class="align-middle"
                    >{{costBreakdown.postDaysRent.total | toMoney}}{{currencySign}}</span>
                  </b-col>
                </b-row>
              </div>
            </md-list>
          </md-list-item>
        </md-list>
      </div>
    </b-col>
  </b-row>
</template>

<script>
import { breakDownRentCosts } from "@/platform/payments/rent/strategies/rentPaymentBreakdownStrategy.js";
import InfoToggle from "@/components/interface/buttons/infoToggle.vue";
import { mapActions, mapState, mapGetters } from "vuex";
export default {
  props: {
    dueNow: {
      type: String,
      required: false,
    },
    terms: {
      type: Object,
      required: false,
    },
    showBookingFee: {
      type: Boolean,
      default: true,
      required: false,
    },
    renderAsExpanded: {
      type: Boolean,
      default: false,
      required: false,
    },
  },
  components: { InfoToggle },
  data() {
    return {
      expandNews: false,
      expandSingle: false,
      loadingBreakdown: true,
      errBreakdown: false,
      costBreakdown: {
        totalBeforeMovingIn: undefined,
        dailyRentTotal: undefined,
        preDaysRent: {
          total: undefined,
          daily: undefined,
        },
        postDaysRent: {
          total: undefined,
          daily: undefined,
        },
        preMonth: undefined,
        postMonth: undefined,
        stayTimeBreakdown: {
          preDays: {
            daysInMonth: undefined,
            nrOfDaysStaying: undefined,
          },
          postDays: {
            daysInMonth: undefined,
            nrOfDaysStaying: undefined,
          },
          totalNrOfDays: undefined,
        },
        price: undefined,
        discountedPrice: undefined,
        deposit: undefined,
        additionalCostsTotal: undefined,
      },
      cleaningFee: 0,
      currencySign: "",
      bookingTitle: "",
    };
  },
  filters: {
    capitalize: function (value) {
      if (!value) return "";
      value = value.toString();
      return value.charAt(0).toUpperCase() + value.slice(1);
    },
    toMoney: function (value) {
      try {
        return Number(value).toFixed(2);
      } catch (ex) {
        return value;
      }
    },
  },
  watch: {
    terms: function (val, oldVal) {
      val ? this.performBreakdown(val) : void 0;
    },
    fees: function (value) {
      this.performBreakdown(this.terms);
    }
  },
  computed: {
    ...mapGetters("internationalizationModule", ["i18n_local"]),
    fees() {
        return this.$store.getters.fees;
    },
  },
  created() {
    this.expandNews = this.renderAsExpanded;
    if (this.dueNow == "booking") {
      this.bookingTitle = this.i18n_local.due_now;
    } else if ((this.dueNow = "before")) {
      this.bookingTitle = this.i18n_local.booking_fee;
    }
    this.terms ? this.performBreakdown(this.terms) : void 0;
  },
  methods: {
    getAccountingPeriod(terms){
      if(!terms.accountingPeriod) return ""
      return terms.accountingPeriod.endDay + "th - "+ terms.accountingPeriod.startDay + "th of the month" 
    },
    performBreakdown(terms) {
      this.setPricingDetails(terms);
      this.loadingBreakdown = true;

      breakDownRentCosts(terms, this.fees)
        .then((breakdown) => {
          this.costBreakdown = breakdown;
          this.errBreakdown = false;
        })
        .catch((err) => {
          this.errBreakdown = true;
        })
        .finally((hi) => {
          this.loadingBreakdown = false;
        });
    },
    setPricingDetails(terms) {
      if (terms.currency && terms.currency == "usd") this.currencySign = "$";
      else this.currencySign = "€";
    },
  },
};
</script>
<style scoped>
@import "../../assets/styles/dashboard.css";
@import "../../assets/styles/booking.css";
@import "../../assets/styles/global.css";
@import "../../assets/styles/listings-view.css";
</style>



// WEBPACK FOOTER //
// src/components/payments/dealPaymentTimeline.vue